import {
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Grid,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import { useCallback, useMemo, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  useAppStore
} from '../../../../App.store'
import { DraftSectionItem } from '../../../../components/Section/Section.model'
import {
  InputMaybe,
  LoopBasis,
  MatrixLoopingConfigInput
} from '../../../../data/gql-gen/questionnaire/graphql'
import { EntryType } from '../../../../data/model/questionnaire'
import useGetDraftQuestionnaire from '../../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { isFocaldataUser } from '../../../../utils/authUtils'
import { useHasEarlierWaves } from '../../Kpi/HasEarlierWavesContext'
import {
  LOOPED_SECTION_KPI_REASON,
  describeEntriesKpiImpact,
  getEntriesKpiImpact
} from '../../Kpi/kpiImpact'
import {
  useEntryByEntryNumber,
  useLoopingSelectedEntry,
  useLoopingSourceResponseOptions,
  useSetLooping
} from '../Looping.hooks'
import { LoopingConfig } from '../Looping.model'
import {
  closeLoopingDialog,
  selectCurrentLoopingSectionNumberRequired,
  selectInitialSourceEntryNumber,
  selectLoopBasis,
  selectLoopRandomisationEnabled,
  selectMatrixLoopingConfig,
  selectMaxIterations,
  selectPrioritizedResponseOptionIds,
  selectSourceChosen,
  selectSourceEntryNumber,
  selectStaticLoopingEnabled
} from '../Looping.slice'
import { LoopingHint } from '../LoopingHint'
import { LoopingSourceSelector } from '../LoopingSourceSelector/LoopingSourceSelector'
import { LoopingMatrixContent } from './LoopingMatrixContent'
import { LoopingMultiSelectContent } from './LoopingMultiSelectContent'
import { StaticLoopingSection } from './StaticLoopingSection'

export const DialogLooping = () => {
  const sectionEntryNumber = useAppSelector(
    selectCurrentLoopingSectionNumberRequired
  )
  const dispatch = useAppDispatch()
  const sourceEntry = useLoopingSelectedEntry()
  const initialSourceEntryNumber = useAppSelector(
    selectInitialSourceEntryNumber
  )
  const initialSourceEntry = useEntryByEntryNumber(initialSourceEntryNumber)
  const sourceEntryType = sourceEntry?.entryType

  const setLooping = useSetLooping({
    sectionEntryNumber,
    sourceEntry,
    prevSourceEntry: initialSourceEntry
  })
  const loopingSourceResponseOptions = useLoopingSourceResponseOptions()
  const store = useAppStore()
  const { draftQuestionnaire } = useGetDraftQuestionnaire()
  const hasEarlierWaves = useHasEarlierWaves()
  const [isKpiConfirmOpen, setIsKpiConfirmOpen] = useState(false)

  const kpiImpact = useMemo(() => {
    const sectionEntry = draftQuestionnaire?.entries.find(
      (entry) => entry.number === sectionEntryNumber
    )
    return getEntriesKpiImpact(
      (sectionEntry?.entryItem as DraftSectionItem | undefined)?.entries ?? []
    )
  }, [draftQuestionnaire?.entries, sectionEntryNumber])

  const handleCloseDialog = useCallback(() => {
    dispatch(closeLoopingDialog())
  }, [dispatch])

  const saveLooping = useCallback(async () => {
    const state = store.getState()

    const loopingSubConfig: Pick<
      LoopingConfig,
      'loopBasis' | 'matrixLoopingConfig'
    > = {
      loopBasis: selectLoopBasis(state)
    }

    if (sourceEntryType === EntryType.MatrixEntryType) {
      // in case looping source is matrix, loopBasis is always `QuotaTarget` and cannot be configured via UI
      loopingSubConfig.loopBasis = LoopBasis.QuotaTarget
      // `matrixLoopingConfig` is only used for matrix source
      loopingSubConfig.matrixLoopingConfig = selectMatrixLoopingConfig(state)
    }

    await setLooping({
      loopingConfig: {
        maxIterations: selectMaxIterations(state),
        sourceEntryNumber: selectSourceEntryNumber(state),
        loopRandomisationEnabled: selectLoopRandomisationEnabled(state),
        prioritizedResponseOptionIds: selectPrioritizedResponseOptionIds(state),
        ...loopingSubConfig,
        loopBasis: LoopBasis[loopingSubConfig.loopBasis],
        // @todo legacy type error – fix this when updating
        matrixLoopingConfig:
          loopingSubConfig.matrixLoopingConfig as InputMaybe<MatrixLoopingConfigInput>,
        loopSpecificDisplayLogics:
          state.looping.loopingConfig.loopSpecificDisplayLogics
      },
      enabled: true,
      enableStaticLooping: selectStaticLoopingEnabled(state)
    })

    handleCloseDialog()
  }, [handleCloseDialog, setLooping, sourceEntryType, store])

  const handleSaveLooping = () => {
    if (kpiImpact.length > 0) {
      setIsKpiConfirmOpen(true)
      return
    }
    saveLooping()
  }

  const closeKpiConfirm = () => setIsKpiConfirmOpen(false)

  const wasSourceEntrySelected = useAppSelector(selectSourceChosen)

  const { classes: textClasses, cx: classNames } =
    textStyleUtils.useTextStyles()

  return (
    <Dialog
      title="Looping"
      fullWidth
      maxWidth={DialogMaxWidth.Sm}
      open
      onClose={handleCloseDialog}
      primaryButtonText="Save"
      primaryButtonDisabled={!wasSourceEntrySelected}
      primaryButtonClick={handleSaveLooping}
      statusComponent={wasSourceEntrySelected ? <LoopingHint /> : null}
      variant={
        wasSourceEntrySelected
          ? DialogVariant.SingleButtonStatus
          : DialogVariant.SingleButton
      }
    >
      <Grid container>
        <p
          className={classNames(
            textClasses.default,
            textClasses.sizeM,
            textClasses.highlightBackground,
            textClasses.gutterBottom
          )}
        >
          Loop based on a question
        </p>
        <Grid container>
          <Grid item xs>
            <LoopingSourceSelector />
          </Grid>
        </Grid>
        {isFocaldataUser() && <StaticLoopingSection />}
      </Grid>
      {sourceEntryType === EntryType.MatrixEntryType && (
        <LoopingMatrixContent />
      )}
      {sourceEntryType === EntryType.QuestionEntryType &&
        loopingSourceResponseOptions.length > 0 && (
          <LoopingMultiSelectContent />
        )}
      <Dialog
        title="Loop this section?"
        open={isKpiConfirmOpen}
        onClose={closeKpiConfirm}
        variant={DialogVariant.DoubleButton}
        primaryButtonText="Add looping"
        primaryButtonClick={() => {
          closeKpiConfirm()
          saveLooping()
        }}
        secondaryButtonText="Cancel"
        secondaryButtonClick={closeKpiConfirm}
      >
        <p
          className={classNames(
            textClasses.default,
            textClasses.weightSemiBold
          )}
        >
          {`${LOOPED_SECTION_KPI_REASON} ${describeEntriesKpiImpact(
            kpiImpact,
            hasEarlierWaves
          )}`}
        </p>
      </Dialog>
    </Dialog>
  )
}
